/* eslint-disable react-hooks/exhaustive-deps */
// React
import { Fragment, useContext, useEffect, useRef, useState } from "react"

// Components
import { Button, Loading, Modal, PermissionLink } from "components"

// Configs
import { iconMenu, useLogoMenu } from "configs"

// Contexts
import { ThemeContext } from "contexts"

// Icons
import { IoLogOutOutline } from "react-icons/io5"
import { TbMoon, TbSun } from "react-icons/tb"

// Third-Party Libraries
import {
  type Location,
  Link,
  Navigate,
  Outlet,
  useLocation,
  useNavigate
} from "react-router-dom"
import { jwtDecode } from "jwt-decode"

// Types
import type { MenuType, RouteType } from "types"
import versionHistory from "utils/vars/version-history.json"

// Utils
import {
  getBasenamePath,
  thisMonth,
  today,
  useHandleAuth,
  usePermissionRoute,
  useToggle
} from "utils"
import { PERMISION } from "security"

export function Layout(props: {
  module_menu: {
    children?: {
      label: string
      link: string
      permission?: string
    }[]
    label: string
    link: string
    permission?: string
  }[]
  outletClass?: string
  routeList?: {
    [key: string]: RouteType[]
  }
}) {
  // Hooks
  const handleAuth = useHandleAuth()
  const { userTokenData } = useHandleAuth()
  const userToken: any = userTokenData()
  const navigate = useNavigate()
  const location = useLocation()
  const permissionRoute = usePermissionRoute()
  const { theme, handleTheme } = useContext(ThemeContext)
  // Vars
  const currentDate = new Date().toLocaleString("en-us", {
    day: "numeric",
    month: "long",
    year: "numeric"
  })

  useEffect(() => {
    const { companyName } = getBasenamePath()
    const tokenData = handleAuth.userTokenData()

    if (!companyName) {
      return window.location.replace("/guest/subscribe")
    }

    if (companyName !== tokenData?.subdomain) {
      return navigate("/login", { replace: true })
    }
  }, [])

  useEffect(() => {
    const overflowElement = document.querySelector("#overflow-container")
    overflowElement?.scrollTo({ top: 0 })
  }, [location])

  // Functions
  const handleClick = () => {
    // @ts-ignore
    document.activeElement?.blur()
  }
  const getPrivilege = (): string => {
    // Vars
    const token: string | null = window.localStorage.getItem("token")
    var privilege: string = ""

    if (token) {
      // Vars
      const res: {
        all_access?: boolean
        is_admin: boolean
        is_subscribed: boolean
      } = jwtDecode(token)

      if (res.is_subscribed && res.is_admin) {
        privilege = "Super Admin"
      } else if (!res.is_subscribed && res.is_admin && res.all_access) {
        privilege = "Full Access"
      } else if (!res.is_subscribed && res.is_admin && !res.all_access) {
        privilege = "Restricted Access"
      }
    }

    return privilege
  }

  if (!localStorage?.getItem("token")) {
    return <Navigate replace to="/login" />
  }

  const adminPreferenceMenus = [
    {
      label: "Other Modules",
      link: "/setup/preferences/other-modules",
      permission: PERMISION.SUBSCRIBED
    },
  ]

  const processedMenu = props.module_menu.map(menu => {
    if (
      menu.label === "PREFERENCES" &&
      userToken?.subdomain === "admin"
    ) {
      return {
        ...menu,
        children: [
          ...(menu.children ?? []),
        ...adminPreferenceMenus
        ]
      }
    }
  
    return menu
  })

  return (
    <Fragment>
      <section className="h-dvh flex flex-col">
        <header className="z-10">
          <section className="bg-base-200">
            <div className="p-3">
              <section className="flex flex-wrap justify-between items-center">
                <Link to="/">
                  <button
                    type="button"
                    title="Home"
                    className="flex items-center font-semibold ui-text-black gap-1">
                    <div className="size-8 bg-contain logo" />
                    <span className="lg:block hidden">HAWK ERP</span>
                  </button>
                </Link>

                <div className="uppercase ml-[-50px] sm:block hidden">
                  <b>{userToken ? userToken?.company_name : ""}</b>
                </div>

                <div className="flex gap-4 items-center">
                  <section className="flex items-center gap-1">
                    <TbSun />

                    <input
                      type="checkbox"
                      className="toggle"
                      checked={theme === "dark"}
                      onChange={handleTheme}
                    />

                    <TbMoon />
                  </section>

                  <section className="sm:block hidden">
                    <div className="flex gap-2">
                      <small className="text-[11px]">USERNAME</small>
                      <small className="text-[11px]">
                        : {userToken ? userToken.sub : ""}
                      </small>
                    </div>
                    <div className="flex gap-2">
                      <small className="text-[11px]">PRIVILEGE</small>
                      <small className="text-[11px]">: {getPrivilege()}</small>
                    </div>
                  </section>

                  <button
                    type="button"
                    className="btn btn-sm btn-ghost bg-base-300"
                    onClick={handleAuth.logout}>
                    <IoLogOutOutline className="w-5 h-5" />
                  </button>
                </div>
              </section>

              <div className="flex gap-2">
                <small className="text-[11px]">DATE</small>
                <small className="text-[11px]">: {currentDate}</small>
              </div>
            </div>

            <section className="flex items-stretch">
              {iconMenu.map((item, key) => (
                <PermissionLink
                  key={key}
                  to={item.link}
                  className="!w-fit last:border-r-0 border-r border-l-0 border-y-0 border-black dark:border-white"
                  permission={item.permission}
                  onClick={() => {
                    if (item.label === "History") {
                      navigate(0)
                    }
                  }}>
                  <Button
                    type="button"
                    className="btn-ghost no-animation p-2 bg-white dark:bg-black"
                    title={item.label}>
                    <item.icon
                      size={24}
                      className="text-black dark:text-white"
                    />
                  </Button>
                </PermissionLink>
              ))}

              <section className="relative grow flex items-stretch overflow-x-auto no-scrollbar">
                {processedMenu.map((item, key) => {
                  const normalizeLabel = (label: string) => label.replace(/\s+/g, "_").toUpperCase();
                  // Vars
                  const currentLink =
                    props.routeList &&
                    props.routeList?.[normalizeLabel(item?.label)]?.find((item) => {
                      return (
                        item.permission &&
                        permissionRoute.checkPermission(item.permission, {
                          disableAlert: true
                        })
                      )
                    })

                  return (
                    <section
                      key={key}
                      className="grow border-r border-white bg-black text-white">
                      {item.children ? (
                        <Menu
                          data={item}
                          location={location}
                          subdomain={userToken.subdomain}
                          handleClick={handleClick}
                        />
                      ) : (
                        <PermissionLink
                          permission={currentLink?.permission}
                          to={
                            currentLink?.path && item.link !== "#"
                              ? currentLink.path
                              : item.link
                          }>
                          <Button
                            type="button"
                            color="ghost"
                            onClick={() => {
                              localStorage.setItem("dateTypeBalanceSheet", "1")
                              localStorage.setItem("dateTypePNL", "1")
                              localStorage.setItem("DateStartPNL", thisMonth().fromDate)
                              localStorage.setItem("DateEndPNL", thisMonth().toDate)
                              localStorage.setItem("DateStartBalanceSheet", today())
                            }}
                            className={`w-full no-animation hover:bg-primary hover:text-black whitespace-nowrap ${location.pathname.includes(item.link) &&
                              "bg-primary text-black"
                              }`}>
                            {item.label}
                          </Button>
                        </PermissionLink>
                      )}
                    </section>
                  )
                })}
              </section>

              <LogoDropdown handleClick={handleClick} />
            </section>
          </section>
        </header>

        <main
          id="overflow-container"
          className={`grow overflow-auto h-[calc(100dvh-108px-60px)] ${props.outletClass}`}>
          <Outlet />
        </main>

        <div className="border-t-2 bg-base-200 h-[60px] p-3 flex justify-between items-center text-[11px]">
          <Version />
          <div>Copyright © All Rights Reserved</div>
        </div>
      </section>
    </Fragment>
  )
}

function Menu(props: {
  alignRight?: boolean
  data: MenuType
  location: Location
  subdomain?:string
  handleClick: () => void
}) {
  return (
    <div className="dropdown w-full">
      <Button
        tabIndex={0}
        size="sm"
        color={
          props.location.pathname.includes(props.data.label.toLowerCase())
            ? "primary"
            : "ghost"
        }
        className={`!h-[48px] w-full no-animation hover:bg-primary hover:text-black whitespace-nowrap ${props.location.pathname.includes(props.data.link) &&
          "bg-primary ui-text-black"
          }`}>
        {props.data.label}
      </Button>

      <ul
        tabIndex={0}
        className="!fixed dropdown-content z-10 menu p-2 shadow bg-white dark:bg-black ui-text-black rounded-box !w-[200px] overflow-y-auto !flex-nowrap rounded-none"
        style={{ maxHeight: "500px" }}>
        {props.data.children!.map((item, key) => {
          if (item.children) {
            return (
              <li key={key}>
                <details open={false}>
                  <summary>{item.label}</summary>

                  <ul>
                    {item.children.map((item, key) => (
                      <li
                        key={key}
                        className={`border-b border-black dark:border-white ${key === 0 && "border-t"
                          }`}
                        onClick={props.handleClick}>
                        <PermissionLink
                          to={item.link}
                          permission={item.permission}>
                          {item.label}
                        </PermissionLink>
                      </li>
                    ))}
                  </ul>
                </details>
              </li>
            )
          }
          return (
            <li
              key={key}
              onClick={props.handleClick}
              className={`border-b border-black dark:border-white ${key === 0 && "border-t"
                }`}>
              <PermissionLink to={item.link} permission={item.permission}>
                {item.label}
              </PermissionLink>
            </li>
          )
          
          
        })}
      </ul>
    </div>
  )
}


function LogoDropdown(props: {
  handleClick: () => void
}) {
  const ref = useRef<any>()
  const { menu } = useLogoMenu()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const handleOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleOutside)
    return () => document.removeEventListener("mousedown", handleOutside)
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <Button
        type="button"
        className="btn-ghost no-animation p-2 bg-base-200"
        title="About"
        onClick={() => setOpen(prev => !prev)}>
        <div className="size-8 bg-contain logo" />
      </Button>

      {open && (
        <ul
          className="absolute right-full top-0 z-[9999] menu p-2 shadow bg-black text-white dark:bg-white dark:text-black w-[300px] overflow-y-auto flex-nowrap rounded-none"
        >
          <div className="text-base font-bold uppercase mb-1 p-2 bg-black text-white">
            other modules
          </div>
          {menu.map((item, key) => (
            <li
              key={key}
              className="border-b border-white dark:border-slate-950"
              onClick={() => { setOpen(false); props.handleClick() }}
            >
              <PermissionLink
                to={item.link}
                permission={item.permission}
                openInNewTab
              >
                {item.label}
              </PermissionLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Version(): JSX.Element {
  const { data, isLoading } = useList()
  const { isActive, toggle } = useToggle(false)

  const parseVersionKey = (version: string): [number, number, number] => {
    const [year = "0", month = "0", day = "0"] = String(version || "0.0.0")
      .split(".")
      .map((v) => v.trim())
    return [Number(year) || 0, Number(month) || 0, Number(day) || 0]
  }

  const compareVersion = (a: string, b: string): number => {
    const pa = parseVersionKey(a)
    const pb = parseVersionKey(b)
    if (pa[0] !== pb[0]) return pa[0] - pb[0]
    if (pa[1] !== pb[1]) return pa[1] - pb[1]
    return pa[2] - pb[2]
  }

  const getLatestVersionByFormat = (data: ListVersioning[] | undefined) => {
    if (!data || data.length === 0) return null;

    return data.reduce((latest, current) =>
      compareVersion(current.app_version, latest.app_version) > 0 ? current : latest
    );
  };

  // Penggunaan
  const latest = getLatestVersionByFormat(data);

  return (
    <Fragment>
      <button onClick={toggle}>
        Version {latest?.app_version}
      </button>

      {isActive && (
        <Modal isOpen title="Project Version" closeModal={toggle}>
          <Modal.Body>
            {isLoading ? <Loading loading={isLoading} /> :
              <div>
                {data.map((item, key) => (
                  <Fragment key={key}>
                    <div className="text-xl font-bold">{item?.app_version}</div>
                    <div className="version-description" dangerouslySetInnerHTML={{ __html: item.version_description ? item.version_description : "-" }} />
                  </Fragment>
                ))}
              </div>
            }
          </Modal.Body>
        </Modal>
      )}
    </Fragment>
  )
}

const useList = () => {
  const normalizeVersionHistory = (rows: VersionHistoryRow[]): ListVersioning[] =>
    rows.map((item, index) => ({
      version_id: rows.length - index,
      release_date: item.release_date,
      division: item.division,
      app_version: item.app_version,
      version_description:
        item.version_description ??
        `<ul>${(item.description ?? []).map((txt) => `<li>${txt}</li>`).join("")}</ul>`
    }))

  const [data, setData] = useState<ListVersioning[]>(
    normalizeVersionHistory(versionHistory as VersionHistoryRow[])
  )
  const [isLoading, setLoading] = useState(false)

  const refetch = () => {
    setData(normalizeVersionHistory(versionHistory as VersionHistoryRow[]))
    setLoading(false)
  }

  useEffect(() => {
    setData(normalizeVersionHistory(versionHistory as VersionHistoryRow[]))
    setLoading(false)

    return () => {
      setData([])
      setLoading(false)
    }

    // eslint-disable-next-line
  }, [])

  return { data, isLoading, refetch }
}
type ListVersioning = {
  version_id?: number
  release_date: string
  division: string
  app_version: string
  version_description?: string
}

type VersionHistoryRow = {
  release_date: string
  division: string
  app_version: string
  version_description?: string
  description?: string[]
}
